import { Box, Collapse } from '@mui/material'
import { ReactNode, useState } from 'react'
import HistoryIcon from '@mui/icons-material/History'
import CheckIcon from '@mui/icons-material/Check'
import SwapHorizIcon from '@mui/icons-material/SwapHoriz'
import GroupsIcon from '@mui/icons-material/Groups'
import ThermostatIcon from '@mui/icons-material/Thermostat'
import RuleIcon from '@mui/icons-material/Rule'
import { FONT_MONO, FONT_SANS } from './SchedulerDesign'
import {
  DATA_SNAPSHOTS_ITEM,
  NavItem,
  RAW_DATA_ITEMS,
  isSectionHidden,
  RULES_ITEMS,
  RuleNavItem,
  SCHEDULE_ITEMS,
  SECTIONS,
  UTILISATION_ITEMS,
} from './SchedulerInput.sections'
import { DataStatusResponse, useDataStatus } from './Scheduler.data'

/**
 * Sidebar tokens (the "Planner sidebar refinements" spec, §1). Colours are the
 * spec's; sizes are deliberately NOT: the spec measured a screenshot taken
 * with display scaling, so its "today" figures (17px text, 45px rows) read
 * about 1.4x the real CSS. Sizes stay at today's real scale (12.5px items),
 * and the spec's other changes are applied at that scale.
 */
const NAV = {
  teal900: '#0E3B4A',
  teal600: '#1F7A8C',
  bg: '#F3F5F6',
  surface: '#FBFCFC',
  hoverBg: '#EEF2F4',
  activeBg: '#E1EDF0',
  iconTileBg: '#E1EDF0',
  hairline: '#E2E7EA',
  rail: '#DCE3E6',
  ringMuted: '#B7C3C9',
  text: '#2A3A44',
  textMuted: '#5B6A74',
  eyebrow: '#76848D',
  chevron: '#9AA7AF',
  statusOk: '#2E8B6E',
  statusWarn: '#C98A1B',
  statusWarnText: '#8F5E0A',
  statusNeutral: '#9AA7AF',
  countBg: '#EBEFF1',
  rowH: 28,
  /** The data table list is denser than the other nav rows. */
  tableRowH: 26,
  /** One left edge for headers, card, eyebrows, rail and pills. */
  gutter: '12px',
  radius: 6,
  radiusLg: 8,
  ease: '120ms ease',
} as const

const SR_ONLY = {
  position: 'absolute',
  width: 1,
  height: 1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
} as const

const FOCUS_RING = {
  '&:focus-visible': {
    outline: `2px solid ${NAV.teal600}`,
    outlineOffset: '2px',
  },
} as const

const REDUCED_MOTION = {
  '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
} as const

const RULE_ICONS: Record<RuleNavItem['iconKey'], ReactNode> = {
  swap_horiz: <SwapHorizIcon sx={{ fontSize: 14 }} />,
  groups: <GroupsIcon sx={{ fontSize: 14 }} />,
  thermostat: <ThermostatIcon sx={{ fontSize: 14 }} />,
  rule: <RuleIcon sx={{ fontSize: 14 }} />,
}

/**
 * Utilisation views, grouped (spec §7). Short names in the sidebar only: the
 * full names stay in the section model, so page headings and tooltips still
 * say "Asset Utilisation" when a view is opened on its own.
 */
const UTILISATION_GROUPS: { label: string; items: [string, string][] }[] = [
  {
    label: 'Overview',
    items: [
      [SECTIONS.UTIL_ASSET, 'Asset'],
      [SECTIONS.UTIL_DAILY, 'Daily'],
      [SECTIONS.UTIL_CONSTRAINT, 'Constraint'],
      [SECTIONS.UTIL_CREW, 'Crew'],
    ],
  },
  {
    label: 'Breakdown',
    items: [
      [SECTIONS.UTIL_DETAILS, 'Details'],
      [SECTIONS.UTIL_FG, 'Finished goods (FG)'],
      [SECTIONS.UTIL_INPUT, 'Input'],
      [SECTIONS.UTIL_KITCHEN, 'Kitchen'],
      [SECTIONS.UTIL_LINE, 'Line'],
      [SECTIONS.UTIL_CLEANING_SUMMARY, 'Cleaning summary'],
    ],
  },
]

/** Tables a run cannot start without. Inputs is "done" when all have rows. */
const REQUIRED_TABLES = [
  'production-requirements',
  'product-master-data',
  'packing-routes',
]

type StepState = 'done' | 'current' | 'upcoming'
type TableStatus = 'loaded' | 'attention' | 'unknown'

const STEP_STATE_WORDS: Record<StepState, string> = {
  done: 'complete',
  current: 'in progress',
  upcoming: 'not started',
}

/**
 * Step states from the data status. Inputs: the required tables have rows.
 * Schedule: a run has finished and nothing has been edited since (an edit
 * makes the schedule out of date). Utilisation: follows the schedule. The
 * first step that is not done is the current one.
 */
const stepStates = (status?: DataStatusResponse): StepState[] => {
  const tables = status?.tables ?? {}
  const inputsDone = REQUIRED_TABLES.every(
    (api) => (tables[api]?.rowCount ?? 0) > 0
  )
  const edited = Object.values(tables).some((t) => t.modifiedSinceRun)
  const scheduleDone = inputsDone && !!status?.lastRunTimestamp && !edited
  const done = [inputsDone, scheduleDone, scheduleDone]
  const firstOpen = done.indexOf(false)
  return done.map((d, i) =>
    d ? 'done' : i === firstOpen ? 'current' : 'upcoming'
  )
}

const tableStatus = (
  status: DataStatusResponse | undefined,
  api?: string
): TableStatus => {
  const t = api ? status?.tables?.[api] : undefined
  if (!t) return 'unknown'
  if (t.modifiedSinceRun) return 'attention'
  return t.rowCount > 0 ? 'loaded' : 'unknown'
}

const TABLE_STATUS_WORDS: Record<TableStatus, string> = {
  loaded: 'Loaded',
  attention: 'Edited since the last run',
  unknown: 'No data',
}

/** "2h ago", "3d ago". For the snapshot card's status line. */
const timeAgo = (iso: string): string => {
  const ms = Date.now() - new Date(iso).getTime()
  if (Number.isNaN(ms)) return ''
  const minutes = Math.max(0, Math.round(ms / 60000))
  if (minutes < 1) return 'just now'
  if (minutes < 60) return `${minutes}m ago`
  const hours = Math.round(minutes / 60)
  if (hours < 24) return `${hours}h ago`
  return `${Math.round(hours / 24)}d ago`
}

/**
 * 12px chevron, 2px stroke (spec §3). Up while the section is open (click to
 * collapse), turning 180° when it collapses; `right` for the snapshot card.
 */
const Chevron = ({ open, right }: { open?: boolean; right?: boolean }) => (
  <Box
    component="svg"
    viewBox="0 0 12 12"
    aria-hidden
    sx={{
      width: 12,
      height: 12,
      flex: 'none',
      transform: right ? 'rotate(-90deg)' : open ? 'rotate(180deg)' : 'none',
      transition: `transform ${NAV.ease}`,
      ...REDUCED_MOTION,
    }}
  >
    <path
      d="M2.5 4.5 6 8l3.5-3.5"
      fill="none"
      stroke={NAV.chevron}
      strokeWidth={2}
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </Box>
)

const StepBadge = ({ step, state }: { step: string; state: StepState }) => (
  <Box
    component="span"
    aria-hidden
    sx={{
      width: 16,
      height: 16,
      borderRadius: '50%',
      display: 'grid',
      placeItems: 'center',
      flex: 'none',
      fontFamily: FONT_SANS,
      fontSize: 9.5,
      fontWeight: 600,
      boxSizing: 'border-box',
      ...(state === 'done'
        ? { background: NAV.teal900, color: '#fff' }
        : state === 'current'
          ? { border: `1.5px solid ${NAV.teal900}`, color: NAV.teal900 }
          : { border: `1.5px solid ${NAV.ringMuted}`, color: NAV.textMuted }),
    }}
  >
    {state === 'done' ? <CheckIcon sx={{ fontSize: 10 }} /> : step}
  </Box>
)

const CountPill = ({ count }: { count: number }) => (
  <Box
    component="span"
    sx={{
      fontFamily: FONT_MONO,
      fontSize: 10,
      lineHeight: '15px',
      color: NAV.textMuted,
      background: NAV.countBg,
      borderRadius: '999px',
      px: '5px',
    }}
  >
    {count}
  </Box>
)

/** `[badge] LABEL ........ [count] [chevron]` over a hairline (spec §3). */
const SectionHeader = ({
  id,
  step,
  label,
  state,
  count,
  open,
  onToggle,
}: {
  id: string
  step: string
  label: string
  state: StepState
  count?: number
  open: boolean
  onToggle: () => void
}) => (
  <Box
    component="button"
    type="button"
    onClick={onToggle}
    aria-expanded={open}
    aria-controls={`nav-section-${id}`}
    aria-label={`Step ${step}, ${label}, ${STEP_STATE_WORDS[state]}`}
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 1,
      width: `calc(100% - 2 * ${NAV.gutter})`,
      mx: NAV.gutter,
      mt: 1.5,
      mb: 0.75,
      px: 0,
      pt: 0,
      pb: '6px',
      border: 'none',
      borderBottom: `1px solid ${NAV.hairline}`,
      background: 'none',
      cursor: 'pointer',
      textAlign: 'left',
      // A <button> takes the browser's own font, not the sidebar's. Set it
      // here rather than with a blanket `font: inherit` on every button,
      // which outranks each button's own size and weight.
      fontFamily: FONT_SANS,
      ...FOCUS_RING,
    }}
  >
    <StepBadge step={step} state={state} />
    <Box
      component="span"
      sx={{
        flex: 1,
        fontFamily: FONT_SANS,
        fontSize: 10.5,
        fontWeight: 600,
        letterSpacing: '0.07em',
        textTransform: 'uppercase',
        color: state === 'upcoming' ? NAV.textMuted : NAV.teal900,
      }}
    >
      {label}
    </Box>
    {count !== undefined && <CountPill count={count} />}
    <Chevron open={open} />
  </Box>
)

/** Group label with a hairline filling the rest of the row (spec §4, §7). */
const Eyebrow = ({
  label,
  gapAbove,
}: {
  label: string
  gapAbove?: boolean
}) => (
  <Box
    sx={{
      display: 'flex',
      alignItems: 'center',
      gap: 0.75,
      px: NAV.gutter,
      pt: gapAbove ? 1.5 : 0.75,
      pb: 0.5,
    }}
  >
    <Box
      component="span"
      sx={{
        fontFamily: FONT_SANS,
        fontSize: 9.5,
        fontWeight: 600,
        letterSpacing: '0.07em',
        textTransform: 'uppercase',
        color: NAV.eyebrow,
      }}
    >
      {label}
    </Box>
    <Box sx={{ flex: 1, height: '1px', background: NAV.hairline }} />
  </Box>
)

const Label = ({ text, title }: { text: string; title?: string }) => (
  <Box
    component="span"
    title={title ?? text}
    sx={{
      flex: 1,
      minWidth: 0,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    }}
  >
    {text}
  </Box>
)

/** Shared row states (spec §5): hover tint, active pill, focus ring. */
const rowSx = (on: boolean, withMarker: boolean) => ({
  position: 'relative' as const,
  display: 'flex',
  alignItems: 'center',
  gap: '7px',
  width: '100%',
  height: NAV.rowH,
  px: 1,
  border: 'none',
  borderRadius: `${NAV.radius}px`,
  background: on ? NAV.activeBg : 'transparent',
  color: on ? NAV.teal900 : NAV.text,
  fontFamily: FONT_SANS,
  // 12px regular, 500 when active: Archivo is a wide face, and at 12.5px or
  // weight 600 it reads heavy and cuts long table names off.
  fontSize: 12,
  fontWeight: on ? 500 : 400,
  textAlign: 'left' as const,
  cursor: 'pointer',
  transition: `background-color ${NAV.ease}`,
  '&:hover': { background: on ? NAV.activeBg : NAV.hoverBg },
  ...FOCUS_RING,
  ...REDUCED_MOTION,
  // 3px x 14px marker, transparent until active so labels never shift.
  ...(withMarker && {
    '&::before': {
      content: '""',
      width: '3px',
      height: '14px',
      borderRadius: '2px',
      flex: 'none',
      background: on ? NAV.teal900 : 'transparent',
    },
  }),
})

interface SidebarProps {
  selectedSection: string
  onSelect: (id: string) => void
  schedulerId: string
  iteration: string
}

/**
 * Scheduler-input sidebar: numbered stages (Inputs / Schedule / Utilisation),
 * a "Data & Snapshots" card leading Inputs, the data tables on a status rail,
 * and the utilisation views in two groups. Pure presentation over the section
 * model and the data status. Spec: "Planner sidebar refinements".
 */
export const SchedulerInputSidebar = ({
  selectedSection,
  onSelect,
  schedulerId,
  iteration,
}: SidebarProps) => {
  const [open, setOpen] = useState<Record<string, boolean>>({
    inputs: true,
    schedule: true,
    utilisation: false,
  })
  const toggle = (k: string) => setOpen((o) => ({ ...o, [k]: !o[k] }))

  const { data: dataStatus } = useDataStatus(schedulerId, iteration)
  const [inputsState, scheduleState, utilisationState] = stepStates(dataStatus)

  const visibleTables = RAW_DATA_ITEMS.filter(
    (item) => !isSectionHidden(item.id)
  )
  const visibleRules = RULES_ITEMS.filter((item) => !isSectionHidden(item.id))

  const NavRow = ({
    item,
    text,
    icon,
  }: {
    item: NavItem
    text?: string
    icon?: ReactNode
  }) => {
    const on = selectedSection === item.id
    return (
      <Box
        component="button"
        type="button"
        onClick={() => onSelect(item.id)}
        aria-current={on ? 'page' : undefined}
        sx={rowSx(on, true)}
      >
        {icon && (
          <Box
            component="span"
            sx={{ display: 'flex', color: on ? NAV.teal900 : NAV.textMuted }}
          >
            {icon}
          </Box>
        )}
        <Label text={text ?? item.label} title={item.label} />
      </Box>
    )
  }

  /** A data table: the status dot takes the marker's place (spec §5). */
  const TableRow = ({ item }: { item: NavItem }) => {
    const on = selectedSection === item.id
    const status = tableStatus(dataStatus, item.api)
    return (
      <Box
        component="button"
        type="button"
        onClick={() => onSelect(item.id)}
        aria-current={on ? 'page' : undefined}
        sx={{
          ...rowSx(on, false),
          height: NAV.tableRowH,
          gap: '8px',
          // Close to the rail, so the dots read as hanging off it.
          pl: '7px',
        }}
      >
        <Box
          component="span"
          aria-hidden
          sx={{
            width: 6,
            height: 6,
            borderRadius: '50%',
            flex: 'none',
            boxSizing: 'border-box',
            ...(status === 'loaded'
              ? { background: NAV.statusOk }
              : status === 'attention'
                ? { border: `1.5px solid ${NAV.statusWarn}` }
                : { background: NAV.statusNeutral }),
          }}
        />
        <Box component="span" sx={SR_ONLY}>
          {TABLE_STATUS_WORDS[status]}:
        </Box>
        <Label text={item.label} />
        {/* Colour is never the only signal: the attention state says so. */}
        {status === 'attention' && (
          <Box
            component="span"
            title="Edited since the last run"
            sx={{ fontSize: 10.5, color: NAV.statusWarnText, flex: 'none' }}
          >
            Edited
          </Box>
        )}
      </Box>
    )
  }

  const hubOn = selectedSection === DATA_SNAPSHOTS_ITEM.id
  const outdated = Object.values(dataStatus?.tables ?? {}).some(
    (t) => t.modifiedSinceRun
  )
  const lastRun = dataStatus?.lastRunTimestamp

  return (
    <Box
      component="nav"
      aria-label="Planner"
      sx={{
        width: 230,
        minWidth: 230,
        flexShrink: 0,
        borderRight: `1px solid ${NAV.hairline}`,
        background: NAV.bg,
        overflowY: 'auto',
        pb: 2,
        fontFamily: FONT_SANS,
      }}
    >
      {/* ── Step 1 · Inputs ── */}
      <SectionHeader
        id="inputs"
        step="1"
        label="Inputs"
        state={inputsState}
        count={visibleTables.length}
        open={open.inputs}
        onToggle={() => toggle('inputs')}
      />
      <Collapse in={open.inputs} id="nav-section-inputs">
        {/* Data & Snapshots card (spec §6) */}
        <Box sx={{ px: NAV.gutter, mb: 0.5 }}>
          <Box
            component="button"
            type="button"
            onClick={() => onSelect(DATA_SNAPSHOTS_ITEM.id)}
            aria-current={hubOn ? 'page' : undefined}
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              width: '100%',
              p: 1,
              textAlign: 'left',
              cursor: 'pointer',
              fontFamily: FONT_SANS,
              background: hubOn ? NAV.activeBg : NAV.surface,
              border: `1px solid ${hubOn ? NAV.teal600 : NAV.hairline}`,
              borderRadius: `${NAV.radiusLg}px`,
              boxShadow:
                '0 1px 2px rgba(14,59,74,.06), 0 4px 12px rgba(14,59,74,.06)',
              transition: `box-shadow ${NAV.ease}`,
              '&:hover': {
                boxShadow:
                  '0 2px 4px rgba(14,59,74,.08), 0 8px 20px rgba(14,59,74,.08)',
              },
              ...FOCUS_RING,
              ...REDUCED_MOTION,
            }}
          >
            <Box
              component="span"
              sx={{
                width: 24,
                height: 24,
                borderRadius: '5px',
                background: NAV.iconTileBg,
                display: 'grid',
                placeItems: 'center',
                flex: 'none',
              }}
            >
              <HistoryIcon sx={{ fontSize: 14, color: NAV.teal900 }} />
            </Box>
            <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
              <Box
                component="span"
                sx={{
                  display: 'block',
                  fontSize: 12,
                  fontWeight: 600,
                  color: NAV.teal900,
                }}
              >
                {DATA_SNAPSHOTS_ITEM.label}
              </Box>
              <Box
                component="span"
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '5px',
                  fontFamily: FONT_MONO,
                  fontSize: 10,
                  color: NAV.textMuted,
                  whiteSpace: 'nowrap',
                  overflow: 'hidden',
                }}
              >
                <Box
                  component="span"
                  aria-hidden
                  sx={{
                    width: 6,
                    height: 6,
                    borderRadius: '50%',
                    flex: 'none',
                    background: outdated ? NAV.statusWarn : NAV.statusOk,
                  }}
                />
                <Box
                  component="span"
                  sx={{ overflow: 'hidden', textOverflow: 'ellipsis' }}
                >
                  {`Version ${iteration}`}
                  {lastRun ? ` · run ${timeAgo(lastRun)}` : ' · not run yet'}
                  {outdated && (
                    <Box component="span" sx={{ color: NAV.statusWarnText }}>
                      {' · outdated'}
                    </Box>
                  )}
                </Box>
              </Box>
            </Box>
            <Chevron right />
          </Box>
        </Box>

        <Eyebrow label="Data tables" />
        <Box
          sx={{
            ml: NAV.gutter,
            mr: NAV.gutter,
            borderLeft: `2px solid ${NAV.rail}`,
          }}
        >
          {visibleTables.map((item) => (
            <TableRow key={item.id} item={item} />
          ))}
        </Box>

        {/* The heading goes with its items: an orphan heading over an empty
            list reads as a broken section rather than a hidden one. */}
        {visibleRules.length > 0 && (
          <>
            <Eyebrow label="Rules & Constraints" gapAbove />
            <Box sx={{ px: NAV.gutter }}>
              {visibleRules.map((item) => (
                <NavRow
                  key={item.id}
                  item={item}
                  icon={RULE_ICONS[item.iconKey]}
                />
              ))}
            </Box>
          </>
        )}
      </Collapse>

      {/* ── Step 2 · Schedule ── */}
      <SectionHeader
        id="schedule"
        step="2"
        label="Schedule"
        state={scheduleState}
        open={open.schedule}
        onToggle={() => toggle('schedule')}
      />
      <Collapse in={open.schedule} id="nav-section-schedule">
        <Box sx={{ px: NAV.gutter }}>
          {SCHEDULE_ITEMS.map((item) => (
            <NavRow key={item.id} item={item} />
          ))}
        </Box>
      </Collapse>

      {/* ── Step 3 · Utilisation ── */}
      <SectionHeader
        id="utilisation"
        step="3"
        label="Utilisation"
        state={utilisationState}
        count={UTILISATION_ITEMS.length}
        open={open.utilisation}
        onToggle={() => toggle('utilisation')}
      />
      <Collapse in={open.utilisation} id="nav-section-utilisation">
        {UTILISATION_GROUPS.map((group, i) => (
          <Box key={group.label}>
            <Eyebrow label={group.label} gapAbove={i > 0} />
            <Box sx={{ px: NAV.gutter }}>
              {group.items.map(([id, short]) => {
                const item = UTILISATION_ITEMS.find((u) => u.id === id)
                return item ? (
                  <NavRow key={id} item={item} text={short} />
                ) : null
              })}
            </Box>
          </Box>
        ))}
      </Collapse>
    </Box>
  )
}
